import { ChangeDetectorRef, Component, OnDestroy } from '@angular/core';
import { SessionsRequestService } from '@services/sessions/sessions.request.service';
import _ from 'lodash';
import { noop, Subscription } from 'rxjs';

import { User } from '../../models/Core/User';
import { Idea } from '../../models/Sessions/Idea/Idea';
import { Session } from '../../models/Sessions/Session';
import { AdminUserRequestService } from '../../services/admin/users.request.service';
import { BusService } from '../../services/bus.service';
import { EventsService } from '../../services/events.service';
import {
  filterIdeasForTags,
  filterUsersForIds,
  findSessionsforIdeas,
  generateIdeaToSessionMapping,
  generateSessionByIdMapping,
  generateUserByIdMapping,
  generateUserIdToSessionsMapping,
  getCollaboratedIdeasForUser,
  getCollaboratorsAndOriginatorsForIdeas,
  getFilteredSessionsByIds,
  getIdeasForSessions,
  getIdeasForUsers,
  getSessionsForUsers,
  getUsersInCountries,
  getUsersInSessions,
  getValidSessions,
  getValidUsers,
  isValidIdea,
} from './FilterHelper';

@Component({
  selector: 'app-peoplefinder',
  templateUrl: './peoplefinder.component.html',
  styleUrls: ['./peoplefinder.component.scss'],
})
export class PeoplefinderComponent implements OnDestroy {
  sessions: Session[];
  idea: Idea;
  allUsers: User[];
  selectedUser: User;
  selectedUsers: User[] = [];
  selectedIdeas: Idea[];
  allIdeas: any[];
  hoveredUser: User;

  public filteredUsers = [];

  private selectedCountries = [];
  private selectedTags = [];
  private selectedSessionIds = [];
  private selectedUserIds = [];
  loading = true;

  public preFilteredUsersForLocation: User[];
  public preFilteredSessionsForTopics: any[];
  public preFilteredIdeasForTags: any[];
  public preFilteredUsersForUserList: any[];
  public filteredIdeas: any[] = [];
  private dataObject: {
    ideaIdToSession: object;
    sessions: Session[];
    sessionsById: object;
    usersById: object;
    userIdToSessions: object;
    users: User[];
  };
  public ideaIdToSession: object;
  private sessionSubscription: Subscription;

  constructor(
    private bus: BusService,
    private events: EventsService,
    private changeDetectorRef: ChangeDetectorRef,
    sessionsRequestService: SessionsRequestService,
    adminUserRequestService: AdminUserRequestService,
  ) {
    adminUserRequestService.getAll().subscribe(noop);
    this.bus.subscribe(
      this.events.received.data.user.all.success,
      this.usersReceive.bind(this),
    );
    this.bus.publish(this.events.requested.data.user.all);
    this.sessionSubscription = sessionsRequestService
      .getAll()
      .subscribe(sessions => this.onSessionsFetched(sessions));
  }

  onSessionsFetched(sessions) {
    this.sessions = getValidSessions(sessions);
    const isLoading = this.allUsers === undefined;

    if (!isLoading) {
      this.onLoaded();
    }
  }

  onUserHovered(user: User) {
    this.hoveredUser = user;
  }

  onUserSelected(users: User[]) {
    this.selectedUsers = users;
    if (users.length > 0) {
      this.selectedUser = users[0];
    } else {
      this.selectedUser = null;
    }

    this.selectedIdeas = getCollaboratedIdeasForUser(
      this.sessions,
      this.selectedUser,
    );
  }

  usersReceive(users: User[]) {
    this.allUsers = users;
    const isLoading = this.sessions === undefined;

    if (!isLoading) {
      this.onLoaded();
    }
  }

  generateMappings() {
    // pre filter to valid users
    this.allUsers = getValidUsers(this.allUsers);
    const userIdToSessionsMapping = generateUserIdToSessionsMapping(
      this.sessions,
      this.allUsers,
    );
    // filter users to those that have at least one ideation session
    this.allUsers = this.allUsers.filter(
      user => userIdToSessionsMapping[user._id].length > 0,
    );
    // filter sessions that have at least one user
    this.sessions = _.uniq(
      _.flatten(this.allUsers.map(user => userIdToSessionsMapping[user._id])),
    );

    this.ideaIdToSession = generateIdeaToSessionMapping(this.sessions);
    const sessionsById = generateSessionByIdMapping(this.sessions);
    const usersById = generateUserByIdMapping(this.allUsers);

    this.dataObject = {
      userIdToSessions: userIdToSessionsMapping,
      ideaIdToSession: this.ideaIdToSession,
      sessionsById: sessionsById,
      usersById: usersById,
      sessions: this.sessions,
      users: this.allUsers,
    };

    // filter to those ideas that have one collaborator in list of all valid users
    this.allIdeas = getIdeasForUsers(
      this.dataObject,
      this.allUsers,
      true,
    ).filter(isValidIdea);
  }

  onCountryFilterChanged(selectedCountries) {
    this.selectedCountries = selectedCountries;
    this.onFilterChanged();
  }

  onTagFilterChanged(selectedTags) {
    this.selectedTags = selectedTags;
    this.onFilterChanged();
  }

  onTopicFilterChanged(selectedSessionIds) {
    this.selectedSessionIds = selectedSessionIds;
    this.onFilterChanged();
  }

  onUserFilterChanged(selectedUserIds) {
    this.selectedUserIds = selectedUserIds;
    this.onFilterChanged();
  }

  onLoaded() {
    this.generateMappings();
    // discard second update from sessions (1st is cached, 2nd is fetched)
    this.unsubscribe();
    this.loading = false;
    this.onFilterChanged();
  }

  onFilterChanged() {
    if (!this.loading) {
      const filteredUsersByCountry = getUsersInCountries(
        this.dataObject,
        this.selectedCountries,
      );
      const filteredUsersBySession = getUsersInSessions(
        this.dataObject,
        this.selectedSessionIds,
      );

      const filteredUsersBySelectedUserIds = filterUsersForIds(
        this.dataObject,
        this.selectedUserIds,
      );

      const filteredSessions = getFilteredSessionsByIds(
        this.dataObject,
        this.selectedSessionIds,
      );

      const filteredIdeasByTag = filterIdeasForTags(
        this.dataObject,
        this.selectedTags,
      );

      let filteredUsersByTag;
      // dont filter users if no tag is selected
      // (otherwise disables those users that are no coll aborator/originator of any idea)
      if (this.selectedTags.length === 0) {
        filteredUsersByTag = this.allUsers;
      } else {
        filteredUsersByTag = getCollaboratorsAndOriginatorsForIdeas(
          this.sessions,
          this.allUsers,
          filteredIdeasByTag,
        );
      }

      // filter users (participants) for selected sessions
      // filter users (collaborators, originators) for a given tag
      this.preFilteredUsersForLocation = _.intersection(
        filteredUsersBySelectedUserIds,
        filteredUsersBySession,
        filteredUsersByTag,
      );

      const intersectionCountryAndUserIds = _.intersectionBy(
        filteredUsersByCountry,
        filteredUsersBySelectedUserIds,
        (user: User) => user._id,
      );

      // filter ideas of users (collaborators, originators) for selected tags
      // filter ideas of users (participants) of given country
      const sessionsForTags = findSessionsforIdeas(
        this.dataObject,
        filteredIdeasByTag,
      );

      const sessionsForUsersInCountryAndForUserIds = getSessionsForUsers(
        this.dataObject,
        intersectionCountryAndUserIds,
      );

      this.preFilteredSessionsForTopics = _.intersectionBy(
        sessionsForTags,
        sessionsForUsersInCountryAndForUserIds,
        (session: Session) => session._id,
      );

      // filter ideas from users (collaborators, originators) of selected countries
      // filter ideas from selected sessions
      const ideasForUsersOfCountryAndUserIdsFilter = getIdeasForUsers(
        this.dataObject,
        intersectionCountryAndUserIds,
        true,
      );
      const ideasForSessionFilter = getIdeasForSessions(filteredSessions);

      this.preFilteredIdeasForTags = _.intersectionBy(
        ideasForUsersOfCountryAndUserIdsFilter,
        ideasForSessionFilter,
        (idea: Idea) => idea._id,
      ); // strangely does not work with intersect after reloading component
      this.preFilteredUsersForUserList = _.intersection(
        filteredUsersByCountry,
        filteredUsersBySession,
        filteredUsersByTag,
      );

      this.filteredIdeas = _.intersectionBy(
        filteredIdeasByTag,
        this.preFilteredIdeasForTags,
        idea => idea._id,
      );

      this.filteredUsers = _.intersection(
        intersectionCountryAndUserIds,
        filteredUsersBySession,
        filteredUsersByTag,
      );

      if (!this.changeDetectorRef['destroyed']) {
        this.changeDetectorRef.detectChanges();
      }
    }
  }

  unsubscribe() {
    this.bus.unsubscribe(
      this.events.received.data.user.all.success,
      this.usersReceive.bind(this),
    );
    if (this.sessionSubscription) {
      this.sessionSubscription.unsubscribe();
    }
  }

  ngOnDestroy() {
    this.changeDetectorRef.detach();
    this.unsubscribe();
  }
}
